import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import SEO from "../components/SEO";
import { categories } from "../mock";
import { getArticle } from "../articlesData";
import { ArrowRight, ArrowUpRight } from "lucide-react";
import BookmarkButton from "../components/BookmarkButton";

const FEATURED_SLUG = "how-to-develop-self-discipline";

const TopicsLanding = () => {
  const featured = getArticle(FEATURED_SLUG);

  useEffect(() => {
    window.scrollTo({ top: 0 });
  }, []);

  return (
    <div className="bg-[#eceae5] min-h-screen" data-testid="topics-landing-page">
      <SEO title="Topics" description="Everything we're unpacking about the decade of becoming — Lifestyle, Career, Finance, and Relationships." type="website" />
      <Navbar solid />

      {/* Header */}
      <header className="pt-32 md:pt-44 pb-10 md:pb-14">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10">
          <p className="font-sans-ui uppercase tracking-[0.25em] text-sm text-neutral-500">
            Explore
          </p>
          <h1
            className="font-display text-4xl sm:text-5xl lg:text-6xl text-neutral-900 mt-3 leading-[1.05]"
            style={{ fontWeight: 700 }}
          >
            Topics
          </h1>
          <p className="font-display italic text-neutral-600 text-lg md:text-xl mt-4 max-w-xl">
            Everything we&rsquo;re unpacking about the decade of becoming &mdash; sorted into the
            corners of life that matter most in your 20s.
          </p>
        </div>
      </header>

      {/* Featured article */}
      {featured && (
        <section className="pb-12 md:pb-16">
          <div className="max-w-[1400px] mx-auto px-6 md:px-10">
            <p className="font-sans-ui uppercase tracking-[0.2em] text-sm text-neutral-500 mb-5">
              Featured
            </p>
            <Link
              to={`/article/${featured.slug}`}
              className="group grid md:grid-cols-12 gap-6 md:gap-10 items-center"
              data-testid="topics-featured-article"
            >
              <div className="relative w-full aspect-[16/10] overflow-hidden rounded-md md:col-span-5">
                <BookmarkButton type="article" id={featured.slug} />
                <img
                  src={featured.image}
                  alt={featured.title}
                  className="w-full h-full object-cover transition-transform duration-[900ms] group-hover:scale-105"
                />
              </div>
              <div className="md:col-span-7">
                <p className="font-sans-ui text-xs uppercase tracking-widest text-neutral-500">
                  {featured.category.charAt(0) + featured.category.slice(1).toLowerCase()} &middot;{" "}
                  {featured.date} &middot; {featured.readTime}
                </p>
                <h2
                  className="font-display text-2xl md:text-3xl text-neutral-900 mt-3 leading-snug group-hover:opacity-70 transition-opacity"
                  style={{ fontWeight: 700 }}
                >
                  {featured.title}
                </h2>
                <p className="font-display text-base md:text-lg text-neutral-700 mt-3 leading-relaxed max-w-2xl">
                  {featured.excerpt}
                </p>
                <span className="inline-flex items-center gap-2 font-sans-ui text-sm text-neutral-900 mt-4">
                  Read the article
                  <ArrowRight size={15} className="transition-transform group-hover:translate-x-1" />
                </span>
              </div>
            </Link>
          </div>
        </section>
      )}

      {/* Sub-categories */}
      <section className="pb-24 md:pb-32">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10">
          <p className="font-sans-ui uppercase tracking-[0.2em] text-sm text-neutral-500 mb-8 pt-4 border-t border-neutral-300">
            Browse by category
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 md:gap-6">
            {categories.map((cat, i) => (
              <Link
                key={cat.slug}
                to={`/topics/${cat.slug}`}
                className="group relative block overflow-hidden rounded-lg"
                data-testid={`topics-category-${cat.slug}`}
              >
                <div className="w-full aspect-[16/10] overflow-hidden">
                  <img
                    src={cat.image}
                    alt={cat.name}
                    className="w-full h-full object-cover transition-transform duration-[1200ms] ease-out group-hover:scale-110"
                  />
                </div>
                <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/25 to-transparent" />
                <span className="absolute top-5 left-6 font-sans-ui text-sm text-white/70 tracking-[0.2em]">
                  0{i + 1}
                </span>
                <span className="absolute top-5 right-6 h-10 w-10 rounded-full border border-white/40 flex items-center justify-center text-white transition-all duration-500 group-hover:bg-white group-hover:text-neutral-900 group-hover:rotate-45">
                  <ArrowUpRight size={18} />
                </span>
                <div className="absolute inset-x-0 bottom-0 p-6 md:p-8">
                  <h3
                    className="font-display text-3xl md:text-[40px] text-white tracking-tight leading-none transition-transform duration-500 group-hover:-translate-y-1"
                    style={{ fontWeight: 700 }}
                  >
                    {cat.name.charAt(0) + cat.name.slice(1).toLowerCase()}
                  </h3>
                  <p className="font-display text-white/85 mt-3 leading-relaxed text-sm md:text-base max-w-sm">
                    {cat.tagline}
                  </p>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default TopicsLanding;
